import React, { useCallback, useEffect, useState } from "react";
import { Modal } from "@/common/components/modal";
import { SuccessModal } from "@/pages/register/success-modal";
import {
  COMPANY_TYPES_URL,
  PRODUCT_TYPE_URL,
  REGISTER_USER_URL,
} from "@/common/constants";
import useQuery from "@/hooks/useQuery";
import { useNotification } from "@/common/components/Notifications/NotificationProvider";
import Form, { FormInput } from "@/common/components/form/Form";
import { User } from "@/common/types";
import { useApiCallHandling } from "@/hooks/useApiCallHandling";
import { getProductList } from "@/pages/complete-registration/subs/data";
import { useSearchParams } from "react-router-dom";
import { ICompanyType } from "@/types/user.interface";

export const formInputs: FormInput[] = [
  {
    inputType: "input",
    loadingState: true,
    prompt: "Company type type",
    name: "company_type",
    placeholder: "Loading company types",
    fullWidth: false,
    readOnly: true,
  },
  {
    inputType: "input",
    loadingState: true,
    prompt: "Product type",
    name: "product_type_id",
    placeholder: "Loading product types",
    fullWidth: false,
    readOnly: true,
  },
  {
    inputType: "email",
    prompt: "Company email address",
    name: "email",
    placeholder: "Enter your company's email address",
    fullWidth: false,
    rules: {
      isEmail: {
        expectedValue: true,
        errorMessage: "Not a valid email address",
      },
    },
  },
  {
    inputType: "text",
    prompt: "Company Phone number",
    name: "phone",
    placeholder: "Enter the company's phone number",
    fullWidth: false,
    rules: {
      validNigerianPhoneNumber: {
        expectedValue: true,
        errorMessage: "Invalid Nigerian phone number",
      },
    },
  },

  {
    inputType: "password",
    prompt: "Password",
    name: "password",
    placeholder: "Enter password",
    fullWidth: false,
    rules: {
      containSpecialChar: {
        expectedValue: true,
        errorMessage: "Include a special character",
      },
      minLength: {
        expectedValue: 8,
        errorMessage: "Should be atleast 8 characters",
      },
      containUppercase: {
        expectedValue: true,
        errorMessage: "Include uppercase letter",
      },
      containLowercase: {
        expectedValue: true,
        errorMessage: "Include lowercase letter",
      },
    },
  },
  {
    inputType: "password",
    prompt: "Confirm",
    name: "repeatPassword",
    placeholder: "Confirm password",
    fullWidth: false,
    rules: {
      compareWithExisting: {
        expectedValue: "password",
        errorMessage: "Does not match the password",
      },
    },
  },
];

export interface IStep1Data {
  email: string;
  phone_number?: number | undefined;
  company_type_id: string;
  product_type_id: string;
}

interface IUserDetail {
  handleChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => any;
  formData: IStep1Data;
}

function UserDetail({}: IUserDetail) {
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [actualForm, setActualForm] = useState<any>([]);
  const [, setFetching] = useState(false);
  const [loading, setLoading] = useState(false);
  const [, setRoles] = useState<any>([]);
  const [, setLoadingRolesFailed] = useState(false);

  const [searchParams] = useSearchParams();

  const paramEmail = searchParams.get("email");
  const paramCompanyType = searchParams.get("company_type");

  const notify = useNotification();

  const [isModalOpen] = useState(true);

  if (!isModalOpen) return null;
  const makeRequest = useApiCallHandling();

  const {
    loading: companyTypesLoading,
    data: companyTypes,
    error: companyTypesError,
  } = useQuery<ICompanyType[]>(COMPANY_TYPES_URL);

  const handleFetchProduct = useCallback(
    (url: string) => {
      makeRequest(
        PRODUCT_TYPE_URL + `?company_type=${url}`,
        "GET",
        {},
        (data) => {
          const productType = {
            inputType: "selectInput",
            loadingState: false,
            returnId: true,
            prompt: "Product type",
            name: "product_type_id",
            placeholder: "Select Product Type",
            fullWidth: false,
            options: getProductList(data?.categories),
          };

          setActualForm((prev: any) => {
            const copy = [...prev];
            copy[1] = productType;
            return copy;
          });
        },
        console.log,
        console.log,
        {
          dontNotifyOnSucess: true,
        }
      );
    },
    [makeRequest]
  );

  const findAndUpdateCompanyTypeOptions = (companyType: string) => {
    const result = companyTypes?.find(
      (type: any) => type.title === companyType
    );

    if (result) {
      const companyTypeInput = {
        inputType: "selectInput",
        returnId: true,
        prompt: "Company type",
        name: "company_type",
        placeholder: result.title,
        fullWidth: false,
        options: [...companyTypes],
        readOnly: true,
        initialValue: result.id,
      };

      setActualForm((prev: any) => {
        const copy = [...prev];
        copy[0] = companyTypeInput;
        return copy;
      });

      handleFetchProduct(result.title.toLowerCase());
    }
  };

  useEffect(() => {
    if (paramEmail && paramCompanyType) {
      let baseFormInputs = [...formInputs];

      if (paramEmail) {
        baseFormInputs[2] = {
          inputType: "text",
          prompt: "Company email address",
          name: "email",
          fullWidth: false,
          readOnly: true,
          placeholder: paramEmail,
          loadingState: false,
          initialValue: paramEmail,
        };

        setActualForm(baseFormInputs);
      }

      if (paramCompanyType && companyTypes?.length > 0) {
        findAndUpdateCompanyTypeOptions(paramCompanyType);
      }

      return;
    }

    setFetching(true);
    setActualForm(formInputs);
    setRoles(companyTypes);

    if (!companyTypesLoading && companyTypes?.length > 0) {
      const companyType = {
        inputType: "selectInput",
        returnId: true,
        prompt: "Company type",
        name: "company_type",
        placeholder: "Select Company Type",
        fullWidth: false,
        options: companyTypes,
      };

      setActualForm((prev: any) => {
        const copy = [...prev];
        copy[0] = companyType;
        return copy;
      });
    }

    if (!companyTypesLoading && companyTypesError) {
      notify({
        type: "ERROR",
        message: "Unable to load roles, refresh the page to try again.",
      });
      setLoadingRolesFailed(true);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyTypes, companyTypesError, companyTypesLoading, paramCompanyType]);

  const onClose = useCallback(() => {
    setShowSuccessModal(false);
    window.location.href = "/";
  }, []);

  async function processFormInputs(values: User) {
    let {
      email,
      company_type,
      product_type_id,
      phone,
      password,
      repeatPassword,
    } = values;
    let referer = "";

    setLoading(true);
    makeRequest(
      REGISTER_USER_URL,
      "POST",
      {
        email,
        password,
        password_confirmation: repeatPassword,
        phone_number: phone,
        company_type_id: company_type,
        product_type_id,
        invited_by: referer,
      },
      () => setShowSuccessModal(true),
      console.log,
      () => setLoading(false)
    );
  }

  const handleFormUpdate = (name: string, value: any) => {
    if (name === "company_type") {
      const userSelection = companyTypes.find((type: any) => type.id === value);
      if (userSelection) {
        handleFetchProduct(userSelection.title.toLowerCase());
        // console.log("Selected company type:", userSelection.title); // Debug log
        // setCompanyTypeValue(userSelection.title.toLowerCase());
      }
    }
  };

  return (
    <div>
      <Modal isOpen={showSuccessModal}>
        <SuccessModal onProceedClick={onClose} />
      </Modal>

      <div className="input_container">
        <div className="heading">MEDEX Onboarding</div>
        <div className="sub-heading">
          <div className="stepcounter">Step 1 of 4</div>
          <div className="stepcountTitle">User Details</div>
        </div>
      </div>

      <Form
        formInputs={actualForm}
        loadingState={loading}
        submitLabel="Begin Registration"
        processInputs={processFormInputs}
        login={false}
        biControl={false}
        handleBack={undefined}
        removePadding
        externalUpdateData={handleFormUpdate}
      />
    </div>
  );
}

export default UserDetail;
